/**
 * GMA UFO easter egg
 * Rare (1:100) flyby that lands on Add to cart, then exits toward the top.
 * Force with data-gma-ufo-force while building, or ?gma_ufo=force for a demo.
 */
(() => {
  const HOST_SEL = '[data-gma-ufo-easter-egg]';
  const ATC_SEL = '.gma-purchase-atc-btn, button.product-form__submit, .product-form__submit';
  const CHANCE = 1 / 100;
  const MIN_CART_CENTS = 7500; // $75
  const COOLDOWN_KEY = 'gma-ufo-last-seen';
  const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // one month

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function rand(min, max) {
    return min + Math.random() * (max - min);
  }

  function pick(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  function atcButton() {
    const btn = document.querySelector(ATC_SEL);
    if (!btn) return null;
    const r = btn.getBoundingClientRect();
    if (r.width < 8 || r.height < 8) return null;
    return { el: btn, rect: r };
  }

  /**
   * Landing pad: bottom of the UFO touches the top edge of the ATC button
   * (red zone). Pose (x,y) is the icon centre.
   * @param {number} size
   * @param {number} [along=0.5] horizontal position across the button (0=left, 1=right)
   */
  function landingPad(size, along) {
    const target = atcButton();
    if (!target) return null;
    const { rect } = target;
    const s = size || 44;
    // Keep the craft fully over the button (half-width inset)
    const inset = Math.min(s * 0.45, Math.max(8, rect.width * 0.12));
    const minX = rect.left + inset;
    const maxX = rect.right - inset;
    const t = Math.min(1, Math.max(0, along == null ? 0.5 : along));
    const x = minX + (maxX - minX) * t;
    return {
      x,
      along: t,
      // Icon bottom meets button top; +3px closes a small visual gap above the red zone
      y: rect.top - s / 2 + 3,
      el: target.el,
      rect,
    };
  }

  /** Random horizontal land target across the ATC top edge. */
  function randomLandAlong() {
    // Bias slightly away from the exact edges while still spanning the button
    return rand(0.08, 0.92);
  }

  /** Hover station just above the pad before the slow settle (~5px higher). */
  function hoverStation(pad, size) {
    const s = size || 44;
    return {
      x: pad.x,
      y: pad.y - (s * 0.85 + rand(12, 28) + 5),
    };
  }

  /** Random point just outside the viewport. */
  function offscreenStart(size) {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const pad = size + 24;
    const edge = pick(['left', 'right', 'top', 'bottom']);
    switch (edge) {
      case 'left':
        return { x: -pad, y: rand(pad, h - pad), edge };
      case 'right':
        return { x: w + pad, y: rand(pad, h - pad), edge };
      case 'top':
        return { x: rand(pad, w - pad), y: -pad, edge };
      default:
        return { x: rand(pad, w - pad), y: h + pad, edge };
    }
  }

  function exitNearTop(size) {
    const w = window.innerWidth;
    const pad = size + 40;
    return {
      x: rand(w * 0.15, w * 0.85),
      y: -pad - rand(40, 140),
    };
  }

  function wanderPoint(size) {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const m = size + 16;
    return {
      x: rand(m, Math.max(m + 1, w - m)),
      y: rand(m, Math.max(m + 1, h * 0.65)),
    };
  }

  function setPose(el, x, y, size) {
    el.style.transform = `translate3d(${x - size / 2}px, ${y - size / 2}px, 0)`;
  }

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /** Extend or retract landing gear; waits for the CSS transition. */
  function setLegs(el, extended) {
    const out = Boolean(extended);
    if (out) {
      el.classList.add('is-legs-out');
    } else {
      el.classList.remove('is-legs-out');
    }
    return wait(out ? 680 : 580);
  }

  function animateTo(el, from, to, durationMs, easing) {
    return new Promise((resolve) => {
      const size = el.offsetWidth || 44;
      const anim = el.animate(
        [
          { transform: `translate3d(${from.x - size / 2}px, ${from.y - size / 2}px, 0)` },
          { transform: `translate3d(${to.x - size / 2}px, ${to.y - size / 2}px, 0)` },
        ],
        {
          duration: Math.max(180, durationMs),
          easing: easing || 'cubic-bezier(0.33, 0, 0.2, 1)',
          fill: 'forwards',
        }
      );
      anim.onfinish = () => {
        setPose(el, to.x, to.y, size);
        resolve();
      };
      anim.oncancel = () => resolve();
    });
  }

  /** Gentle vertical bob while holding station above the pad. */
  async function hoverInPlace(el, at, size) {
    const bob = rand(6, 11);
    const up = { x: at.x, y: at.y - bob };
    const down = { x: at.x, y: at.y + bob * 0.35 };
    await animateTo(el, at, up, rand(520, 780), 'ease-in-out');
    await animateTo(el, up, down, rand(640, 920), 'ease-in-out');
    await animateTo(el, down, at, rand(520, 780), 'ease-in-out');
    setPose(el, at.x, at.y, size);
  }

  async function deliberateLand(el, cursor, size, mission) {
    const along = randomLandAlong();
    const pad = landingPad(size, along);
    if (!pad) return cursor;
    if (mission?.aborted) return cursor;

    // 1) Move into position above the chosen spot on the red zone
    const station = hoverStation(pad, size);
    await animateTo(el, cursor, station, rand(1100, 1700), 'cubic-bezier(0.33, 0.1, 0.2, 1)');
    cursor = station;
    if (mission?.aborted) return cursor;

    // 2) Hover / hold station — extend landing gear while hovering
    await setLegs(el, true);
    if (mission?.aborted) return cursor;
    await hoverInPlace(el, cursor, size);
    if (mission?.aborted) return cursor;
    await wait(rand(350, 700));
    if (mission?.aborted) return cursor;

    // Re-measure pad in case the page scrolled (keep the same horizontal slot)
    const padNow = landingPad(size, along) || pad;
    const finalStation = {
      x: padNow.x,
      y: padNow.y - (size * 0.65 + 8 + 5),
    };
    if (Math.abs(finalStation.x - cursor.x) > 4 || Math.abs(finalStation.y - cursor.y) > 4) {
      await animateTo(el, cursor, finalStation, rand(400, 700), 'ease-in-out');
      cursor = finalStation;
      if (mission?.aborted) return cursor;
      await wait(rand(200, 400));
      if (mission?.aborted) return cursor;
    }

    // 3) Slow deliberate settle — bottom of icon meets top of button only
    const touchdown = landingPad(size, along) || padNow;
    await animateTo(
      el,
      cursor,
      { x: touchdown.x, y: touchdown.y },
      rand(1400, 2100),
      'cubic-bezier(0.22, 0.61, 0.2, 1)'
    );
    cursor = { x: touchdown.x, y: touchdown.y };
    if (mission?.aborted) return cursor;
    el.classList.add('is-landed');
    window.dispatchEvent(new CustomEvent('gma:ufo-landed', { bubbles: true }));

    await wait(rand(2400, 4000));
    return cursor;
  }

  async function deliberateTakeoff(el, cursor, size, mission) {
    if (mission?.aborted) return cursor;
    // Glow back on while still on the pad, then lift
    el.classList.add('is-warming');
    await wait(rand(500, 900));
    if (mission?.aborted) return cursor;
    el.classList.remove('is-landed', 'is-warming');
    const lift = { x: cursor.x + rand(-8, 8), y: cursor.y - rand(48, 72) };
    await animateTo(el, cursor, lift, rand(1100, 1600), 'cubic-bezier(0.25, 0.1, 0.2, 1)');
    cursor = lift;
    if (mission?.aborted) return cursor;
    await wait(rand(400, 800));
    if (mission?.aborted) return cursor;
    await hoverInPlace(el, cursor, size);
    if (mission?.aborted) return cursor;
    await setLegs(el, false);

    const climb = {
      x: cursor.x + rand(-40, 40),
      y: Math.max(size + 20, cursor.y - rand(100, 160)),
    };
    await animateTo(el, cursor, climb, rand(900, 1400), 'ease-in-out');
    cursor = climb;
    await wait(rand(250, 500));

    const exit = exitNearTop(size);
    await animateTo(el, cursor, exit, rand(1200, 1800), 'cubic-bezier(0.3, 0, 0.2, 1)');
    return exit;
  }

  function isLocalHostname(hostname) {
    return hostname === '127.0.0.1' || hostname === 'localhost';
  }

  /**
   * Claim endpoint must belong to the storefront the shopper is on.
   * A dev URL in `gma.ufo_claim_url` (or any http URL on an https page) would
   * fail as mixed content / connection refused, so fall back to the app proxy.
   */
  function resolveClaimUrl(host) {
    const fallback = new URL(
      (host.dataset.gmaUfoClaimPath || '/apps/gma-ufo/claim').trim(),
      window.location.origin
    ).toString();
    const raw = (host.dataset.gmaUfoClaimUrl || '').trim();
    if (!raw) return fallback;

    let url;
    try {
      // Absolute first; only a rooted path may be treated as same-origin.
      url = raw.startsWith('/') ? new URL(raw, window.location.origin) : new URL(raw);
    } catch (_) {
      return fallback;
    }
    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      return fallback;
    }
    if (isLocalHostname(url.hostname) && !isLocalHostname(window.location.hostname)) {
      return fallback;
    }
    if (window.location.protocol === 'https:' && url.protocol === 'http:') {
      return fallback;
    }
    return url.toString();
  }

  async function mintUniqueCode(host) {
    const claimUrl = resolveClaimUrl(host);
    if (!claimUrl) {
      throw new Error('missing claim url');
    }
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
    const secret = (host.dataset.gmaUfoClaimSecret || '').trim();
    if (secret) headers['X-Gma-Ufo-Claim'] = secret;

    const res = await fetch(claimUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ source: 'gma-ufo-easter-egg' }),
    });
    if (!res.ok) {
      throw new Error(`claim failed (${res.status})`);
    }
    const data = await res.json();
    const code = String(data?.code || '').trim();
    if (!/^ufo-sighting-\d+$/i.test(code)) {
      throw new Error('invalid minted code');
    }
    return { code, n: Number(data?.n) || null, discountId: data?.discountId || null };
  }

  async function applyDiscountCode(code) {
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const res = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        discount: code,
        // Cart attribute is Liquid-visible (free-shipping codes are not). Cleared with the cart after checkout.
        attributes: { gma_ufo_sighting: code },
      }),
    });
    if (!res.ok) {
      throw new Error(`cart discount failed (${res.status})`);
    }
    return res.json();
  }

  /** Force/QA path when mint fails: mark cart so van/footnote unlock until checkout clears the cart. */
  async function markUfoSightingOnCart(code) {
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const res = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        attributes: { gma_ufo_sighting: code || 'ufo-sighting-force' },
      }),
    });
    if (!res.ok) {
      throw new Error(`cart attribute failed (${res.status})`);
    }
    return res.json();
  }

  async function grantFreeDelivery(host) {
    const force = host.hasAttribute('data-gma-ufo-force');
    let minted = null;
    let cart = null;

    try {
      minted = await mintUniqueCode(host);
      cart = await applyDiscountCode(minted.code);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gma-ufo', cartData: cart });
      }
    } catch (error) {
      // Force/QA: still unlock van + footnote so sighting copy is testable without claim HTTPS.
      // Production (no force): require a successful unique mint + cart apply.
      if (!force) throw error;
      console.warn('[gma-ufo] claim/apply failed in force mode; marking cart attribute only', error);
      try {
        cart = await markUfoSightingOnCart('ufo-sighting-force');
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gma-ufo', cartData: cart });
        }
      } catch (attrError) {
        console.warn('[gma-ufo] cart attribute mark failed', attrError);
      }
    }

    window.dispatchEvent(
      new CustomEvent('gma:ufo-free-ship-granted', {
        detail: {
          code: minted?.code || cart?.attributes?.gma_ufo_sighting || null,
          n: minted?.n || null,
          discountId: minted?.discountId || null,
          cart,
          uiOnly: !minted,
        },
        bubbles: true,
      })
    );
    return cart;
  }

  function bindCatchHandler(el, mission) {
    const onCatch = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (el.classList.contains('is-claiming') || el.classList.contains('is-claimed')) return;
      el.classList.add('is-claiming');
      grantFreeDelivery(el)
        .then(() => {
          el.classList.remove('is-claiming');
          el.classList.add('is-claimed');
          el.setAttribute('aria-label', 'Free delivery unlocked');
          if (mission && typeof mission.abort === 'function') {
            mission.abort('caught');
          }
        })
        .catch((error) => {
          el.classList.remove('is-claiming');
          console.warn('[gma-ufo] free delivery grant failed', error);
        });
    };
    el.addEventListener('click', onCatch);
    el.addEventListener('touchend', onCatch, { passive: false });
    return () => {
      el.removeEventListener('click', onCatch);
      el.removeEventListener('touchend', onCatch);
    };
  }

  async function flyMission(el) {
    const size = el.offsetWidth || 44;
    if (!landingPad(size)) return;

    const mission = { aborted: false, reason: null, abort(reason) {
      this.aborted = true;
      this.reason = reason || 'aborted';
    } };
    const unbind = bindCatchHandler(el, mission);

    const start = offscreenStart(size);
    setPose(el, start.x, start.y, size);
    el.classList.remove('is-legs-out', 'is-landed', 'is-warming', 'is-claiming', 'is-claimed');
    el.classList.add('is-active');
    el.setAttribute('aria-hidden', 'false');
    el.tabIndex = 0;

    let cursor = start;
    const hops = 3 + Math.floor(Math.random() * 4);

    try {
      for (let i = 0; i < hops; i += 1) {
        if (mission.aborted) break;
        if (Math.random() < 0.45) {
          await wait(rand(280, 1100));
        }
        if (mission.aborted) break;

        const next = wanderPoint(size);
        const pace = Math.random();
        let duration;
        let easing;
        if (pace < 0.28) {
          duration = rand(1400, 2400);
          easing = 'ease-in-out';
        } else if (pace < 0.62) {
          duration = rand(700, 1300);
          easing = 'cubic-bezier(0.4, 0, 0.2, 1)';
        } else {
          duration = rand(320, 680);
          easing = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
        }

        await animateTo(el, cursor, next, duration, easing);
        cursor = next;

        if (mission.aborted) break;
        if (Math.random() < 0.35) {
          await wait(rand(400, 1600));
        }
      }

      if (!mission.aborted) {
        cursor = await deliberateLand(el, cursor, size, mission);
      }
      if (!mission.aborted) {
        await deliberateTakeoff(el, cursor, size, mission);
      } else {
        // Caught — quick climb-away celebration
        el.classList.remove('is-landed', 'is-warming');
        el.classList.add('is-legs-out');
        const exit = exitNearTop(size);
        await animateTo(el, cursor, exit, rand(700, 1100), 'cubic-bezier(0.3, 0, 0.2, 1)');
      }
    } finally {
      unbind();
      el.classList.remove('is-active', 'is-legs-out', 'is-landed', 'is-warming', 'is-claiming');
      el.setAttribute('aria-hidden', 'true');
      el.tabIndex = -1;
      el.style.transform = 'translate3d(-9999px, -9999px, 0)';
    }
  }

  /** Demo trigger: ?gma_ufo=force (or =1) so a flyby can be shown on request. */
  function urlForced() {
    try {
      const value = new URLSearchParams(window.location.search).get('gma_ufo');
      return value === 'force' || value === '1';
    } catch (_) {
      return false;
    }
  }

  function isForced(host) {
    return (
      host.hasAttribute('data-gma-ufo-force') ||
      host.dataset.gmaUfoForce === 'true' ||
      urlForced()
    );
  }

  function minCartCents(host) {
    const raw = Number(host.dataset.gmaUfoMinCart);
    return Number.isFinite(raw) && raw > 0 ? raw : MIN_CART_CENTS;
  }

  async function fetchCartTotalCents() {
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cartUrl = window.routes?.cart_url ? `${window.routes.cart_url}.js` : `${root}cart.js`;
    const response = await fetch(cartUrl, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('Unable to load cart');
    const cart = await response.json();
    return Number(cart.total_price || 0);
  }

  async function cartMeetsMinimum(host) {
    try {
      const total = await fetchCartTotalCents();
      return total >= minCartCents(host);
    } catch (_) {
      return false;
    }
  }

  function readLastSeen() {
    try {
      const raw = localStorage.getItem(COOLDOWN_KEY);
      if (!raw) return null;
      const ts = Number(raw);
      return Number.isFinite(ts) ? ts : null;
    } catch (_) {
      return null;
    }
  }

  function markSeen() {
    try {
      localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
    } catch (_) {
      /* private mode / blocked storage */
    }
  }

  function isOnCooldown() {
    const last = readLastSeen();
    if (last == null) return false;
    return Date.now() - last < COOLDOWN_MS;
  }

  /** Chance + cooldown only. Cart minimum is checked separately. */
  function passesChanceRoll(host) {
    if (isForced(host)) return true;
    if (isOnCooldown()) return false;
    const chance = Number(host.dataset.gmaUfoChance || CHANCE);
    if (chance >= 1) return true;
    return Math.random() < chance;
  }

  async function boot() {
    const host = document.querySelector(HOST_SEL);
    if (!host) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!(await cartMeetsMinimum(host))) return;
    if (!passesChanceRoll(host)) return;

    // Wait a few seconds so the page settles before the cameo
    const delay = isForced(host) ? rand(3500, 5500) : rand(4000, 9000);
    window.setTimeout(async () => {
      // Re-check cart in case it dropped below $75 during the delay
      if (!(await cartMeetsMinimum(host))) return;
      if (!landingPad(host.offsetWidth || 44)) return;
      if (!isForced(host)) markSeen();
      flyMission(host).catch(() => {
        host.classList.remove('is-active', 'is-legs-out', 'is-landed', 'is-warming');
      });
    }, delay);
  }

  /**
   * Manual trigger for demos: window.gmaUfo.fly() in the console.
   * Skips chance, cooldown and the cart minimum — nothing runs until it is called.
   */
  window.gmaUfo = {
    fly() {
      const host = document.querySelector(HOST_SEL);
      if (!host || host.classList.contains('is-active')) return false;
      if (!landingPad(host.offsetWidth || 44)) return false;
      flyMission(host).catch(() => {
        host.classList.remove('is-active', 'is-legs-out', 'is-landed', 'is-warming');
      });
      return true;
    },
  };

  ready(() => {
    boot().catch(() => {});
  });
})();
